import { useEffect } from 'react';
import { observer } from 'mobx-react';
import { AutocompleteRenderOptionState } from '@material-ui/lab';
import {
  Avatar,
  Box,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core';
import { Search } from '@material-ui/icons';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { VirtualizedAutocomplete } from '@roc/ui';
import { InsuranceAgentDirectoryStore } from '../stores/insuranceAgentDirectoryStore';
import {
  InsuranceAgentRecord,
  getInitials,
  highlightEmail,
} from '../utils/insuranceAgentDirectoryUtils';

const MIN_QUERY_LENGTH = 2;
const OPTION_HEIGHT = 72;

const useStyles = makeStyles((theme: Theme) => ({
  avatar: {
    width: 36,
    height: 36,
    fontSize: 12.5,
    fontWeight: 700,
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
  },
  highlight: {
    backgroundColor: 'rgba(46,98,255,0.16)',
    borderRadius: 3,
    fontWeight: 700,
  },
  dropdownHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.grey[50],
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  optionRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    width: '100%',
    minWidth: 0,
  },
}));

interface AddNewAgentOption {
  isAddNew: true;
  name: string;
}

type AgentOption = InsuranceAgentRecord | AddNewAgentOption;

const isAddNewOption = (option: AgentOption): option is AddNewAgentOption =>
  !!option && (option as AddNewAgentOption).isAddNew === true;

export interface AgentDirectorySearchProps {
  store: InsuranceAgentDirectoryStore;
  label?: string;
  placeholder?: string;
  value: string;
  error?: string;
  disabled?: boolean;
  onQueryChange: (value: string) => void;
  onSelect: (agent: InsuranceAgentRecord) => void;
  onCreateNew?: (name: string) => void;
}

export const AgentDirectorySearch = observer((props: AgentDirectorySearchProps) => {
  const {
    store,
    label,
    placeholder,
    value,
    error,
    disabled,
    onQueryChange,
    onSelect,
    onCreateNew,
  } = props;
  const classes = useStyles();

  useEffect(() => {
    if (value && !store.query) {
      store.seedQuery(value);
    }
  }, []);

  const handleInputChange = (event: any, newValue: string, reason: string) => {
    if (reason === 'input') {
      store.setQuery(newValue);
      onQueryChange(newValue);
    }
  };

  const handleChange = (event: any, option: AgentOption | string) => {
    if (!option || typeof option === 'string') {
      return;
    }
    if (isAddNewOption(option)) {
      onCreateNew?.(option.name);
      return;
    }
    store.selectAgent(option);
    onSelect(option);
  };

  const filterOptions = (options: InsuranceAgentRecord[]): AgentOption[] => {
    if (
      options.length === 0 &&
      !store.isLoading &&
      store.query.trim().length >= MIN_QUERY_LENGTH
    ) {
      return [{ isAddNew: true, name: store.query.trim() }];
    }
    return options;
  };

  const renderOption = (
    option: AgentOption,
    state: AutocompleteRenderOptionState
  ) => {
    if (isAddNewOption(option)) {
      return (
        <Box display="flex" flexDirection="column" py={0.5}>
          <Typography variant="body2" color="textSecondary">
            No agent matches &ldquo;{option.name}&rdquo;
          </Typography>
          <Typography variant="body2" color="primary" style={{ fontWeight: 600 }}>
            {`+ Add "${option.name}" as a new agent`}
          </Typography>
        </Box>
      );
    }
    const { pre, mid, post } = highlightEmail(option.email, store.query);
    return (
      <div className={classes.optionRow}>
        <Avatar className={classes.avatar}>{getInitials(option.name)}</Avatar>
        <Box minWidth={0} flex={1}>
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            {option.name}
          </Typography>
          <Typography
            variant="body2"
            color="primary"
            style={{ wordBreak: 'break-all' }}
          >
            {pre}
            <span className={classes.highlight}>{mid}</span>
            {post}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {option.phone}
            {option.agency ? ` · ${option.agency}` : ''}
          </Typography>
        </Box>
      </div>
    );
  };

  const PaperComponent = ({ children, ...paperProps }: any) => (
    <Paper {...paperProps}>
      {store.matchCount > 0 && (
        <Box className={classes.dropdownHeader}>
          <Typography variant="caption" style={{ fontWeight: 600, letterSpacing: '0.07em' }}>
            {store.matchCount === 1 ? '1 MATCHING AGENT' : `${store.matchCount} MATCHING AGENTS`}
          </Typography>
          {store.showsScrollHint && (
            <Typography variant="caption" color="textSecondary">
              Scroll for more
            </Typography>
          )}
        </Box>
      )}
      {children}
    </Paper>
  );

  return (
    <VirtualizedAutocomplete
      optionHeight={OPTION_HEIGHT}
      freeSolo
      disabled={disabled}
      options={store.results}
      value={null}
      loading={store.isLoading}
      open={store.isOpen && !disabled}
      onOpen={() => store.openDropdown()}
      onClose={() => store.closeDropdown()}
      inputValue={store.query}
      onInputChange={handleInputChange}
      onChange={handleChange}
      filterOptions={filterOptions}
      getOptionLabel={(option: AgentOption | string) => {
        if (typeof option === 'string') return option;
        return isAddNewOption(option) ? option.name : option.email;
      }}
      renderOption={renderOption}
      PaperComponent={PaperComponent}
      noOptionsText="Type at least 2 characters to search the directory."
      renderInput={params => (
        <TextField
          {...params}
          label={label ? `${label} *` : undefined}
          placeholder={placeholder}
          variant="outlined"
          fullWidth
          error={!!error}
          helperText={error}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <Search fontSize="small" color="disabled" style={{ marginRight: 8 }} />
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {store.isLoading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
});

export default AgentDirectorySearch;
